/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable ngrx/prefer-action-creator-in-dispatch */
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatCalendarCellClassFunction } from '@angular/material/datepicker';
import { Store } from '@ngrx/store';
import { format, isSameDay } from 'date-fns';
import { sortBy as _sortBy } from 'lodash';
import { Moment } from 'moment';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import { filter, map, startWith, take, takeUntil } from 'rxjs/operators';

import { BillAccountsSelectors } from '@amfam/billing/billaccount/data-access';
import {
  PaymentMethodAccountType,
  PaymentMethodAddEditConfig,
  PaymentMethodAddEditOperationType,
  PaymentMethodUtilService
} from '@amfam/billing/payment-method/util';
import { ScheduledPaymentSelectors } from '@amfam/billing/schedulepayment/data-access';
import { AnalyticsFacade } from '@amfam/shared/analytics';
import {
  BillAccount,
  BillingPaymentPaths,
  MatDatePickerOptions,
  OverviewAnalytics,
  PaymentMethodRadioOption,
  ScheduledPayment
} from '@amfam/shared/models';
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access';
import { ImpersonateRolesService } from '@amfam/shared/utility/impersonation';
import { fromRouterActions, routerQuery } from '@amfam/shared/utility/navigation';
import { CopyService, UtilService } from '@amfam/shared/utility/shared-services';
import {
  AlertModalConfig,
  DockingBarService,
  DsModalService,
  LoadingSpinnerService,
  ValidationService
} from '@amfam/ui-kit';

import {
  AccountForSimplifiedPayment,
  OneTimePayActions,
  OneTimePayFeatureFacade,
  PaymentOption,
  simplifiedPaymentReviewData
} from '../../..';

@Component({
  selector: 'amfam-simplified-payment-choose-account-wrapper',
  templateUrl: './simplified-payment-choose-account-wrapper.component.html',
  styleUrls: ['./simplified-payment-choose-account-wrapper.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class SimplifiedPaymentChooseAccountWrapperComponent implements OnInit, OnDestroy {
  accountSelectionFormGroup: FormGroup;
  paymentMethodAndDateFormGroup: FormGroup;
  billAccountOptions$: Observable<AccountForSimplifiedPayment[]>;
  accountSelectionHeading = this.copyService.getCopy(
    'billing.simplifiedPayment.accountSelectionHeading'
  );
  accountSelectionHeadingP2 = this.copyService.getCopy(
    'billing.simplifiedPayment.accountSelectionHeadingP2'
  );
  accountSelectionSubHeading: string;
  selectedAccounts: {
    billAccountNumber: string;
    selectedPaymentAmount: string;
    dueDate: string;
    paymentTerm: string;
  }[] = [];
  linkEventText: string;
  alertLinkEventBillAccountNumber: string;
  isPhoenix = false;
  isHomesite = false;

  cancelModalConfig: AlertModalConfig = this.oneTimePayFacade.cancelModalConfig();

  silentRegistrationAllFailedError: AlertModalConfig =
    this.oneTimePayFacade.silentRegistrationAllFailedError();

  silentRegistrationPartialError: AlertModalConfig =
    this.oneTimePayFacade.silentRegistrationPartialError();
  datePickerEligibleAndTotal$: Observable<{
    sum: number;
    disableCalender: boolean;
  }>;
  matDatePickerOptions: MatDatePickerOptions;
  todayDate: string = format(new Date(), 'MM/DD/YYYY');
  reviewPaymentInfo: simplifiedPaymentReviewData;
  simplifiedPaymentP2Enabled = this.featureFlag.isEnabled('simplified_payment_p2');
  paymentMethodOptions: PaymentMethodRadioOption[];
  defaultPaymentMethod: PaymentMethodRadioOption;
  authorizedToAddPaymentMethod$: Observable<boolean>;
  authorizedToEditPaymentMethod$: Observable<boolean>;
  paymentMethodAddEditConfig$: BehaviorSubject<PaymentMethodAddEditConfig> = new BehaviorSubject(
    null
  );
  paymentMethodError: boolean;
  scheduledPayments: ScheduledPayment[];
  dupPayments: ScheduledPayment[] = [];
  romeBannerPaymentModalId = 'romeBannerPaymentModalId';
  romeBannerList: string[] = ['Old Billing Account', 'New Billing Account'];
  hasMultiBillAccForRome: boolean;
  addPaymentMethodType: string;
  romeFeatureBanner: boolean;
  romeFeaturePill: boolean;

  private stop$ = new Subject<void>();

  constructor(
    private store: Store,
    private formBuilder: FormBuilder,
    private spinner: LoadingSpinnerService,
    private copyService: CopyService,
    private dockingBar: DockingBarService,
    private modalService: DsModalService,
    public roleService: ImpersonateRolesService,
    private utilService: UtilService,
    private paymentMethodUtil: PaymentMethodUtilService,
    private oneTimePayFacade: OneTimePayFeatureFacade,
    private featureFlag: FeatureFlagService,
    private analyticsFacade: AnalyticsFacade
  ) {}

  ngOnInit(): void {
    this.romeFeatureBanner = this.featureFlag.isEnabled('rome_banner');
    this.romeFeaturePill = this.featureFlag.isEnabled('rome_card_pill');
    this.dockingBar.registerHeading(
      this.copyService.getCopy('billing.simplifiedPayment.dockingBarHeading')
    );
    this.billAccountOptions$ =
      this.oneTimePayFacade.getSimplifiedPaymentBillAccountsWithAlertMessageList();
    this.getForm();
    this.datePickerEligibleAndTotal$ = this.handleCheckBoxInteraction();
    this.authorizedToAddPaymentMethod$ = this.roleService.isAuthorized(
      'makepaymentacct_walkthrough'
    );
    this.authorizedToEditPaymentMethod$ = this.roleService.isAuthorized(
      'editpaymentacct_walkthrough'
    );
    this.billAccountOptions$.subscribe(billAccountOptions => {
      const hasRomeNew = billAccountOptions.some(billAccountOption => billAccountOption.isRomeNew);
      const hasRomeOld = billAccountOptions.some(billAccountOption => billAccountOption.isRomeOld);
      this.hasMultiBillAccForRome = hasRomeNew && hasRomeOld;
    });

    const paymentStartAnalytics = {
      event: 'payment_start',
      payment_term: '',
      payment_type: '',
      payment_step: ''
    };
    this.analyticsFacade.trackEvent(paymentStartAnalytics);

    this.oneTimePayFacade
      .selectIsPhoenixPolicy()
      .pipe(
        map(data => data),
        takeUntil(this.stop$)
      )
      .subscribe(data => {
        this.isPhoenix = data;
      });

    this.store
      .select(BillAccountsSelectors.selectIsSelectedBillPolicyHomesite)
      .pipe(take(1))
      .subscribe(isHomesitePolicy => {
        this.isHomesite = isHomesitePolicy;
      });
  }

  getForm(): void {
    combineLatest([
      this.store.select(routerQuery.getRouterPreviousStateUrl),
      this.billAccountOptions$,
      this.oneTimePayFacade.getOTPEntities(),
      this.oneTimePayFacade.buildPaymentMethodOptions(),
      this.store.select(ScheduledPaymentSelectors.selectScheduledPayments)
    ])
      .pipe(take(1))
      .subscribe(
        ([
          previousUrl,
          billAccountOptions,
          otpEntities,
          paymentMethodOptions,
          scheduledPayments
        ]) => {
          this.paymentMethodOptions = paymentMethodOptions;
          this.scheduledPayments = scheduledPayments;
          const key = Object.keys(otpEntities)[0];
          this.store.dispatch(OneTimePayActions.setOneTimePayCancelPath({ url: previousUrl }));
          this.accountSelectionSubHeading = this.simplifiedPaymentP2Enabled
            ? this.copyService.getCopy(
                'billing.simplifiedPayment.multiAccountSelectionSubHeadingP2'
              )
            : billAccountOptions.length === 1
              ? this.copyService.getCopy(
                  'billing.simplifiedPayment.singleAccountSelectionSubHeading'
                )
              : this.copyService.getCopy(
                  'billing.simplifiedPayment.multiAccountSelectionSubHeading'
                );
          const formControls = billAccountOptions.map(
            billAccountOption =>
              new FormControl({
                value: billAccountOption.isSelected,
                disabled: billAccountOption.isDisabled
              })
          );

          this.accountSelectionFormGroup = this.formBuilder.group(
            {
              option: new FormArray(formControls)
            },
            { validators: ValidationService.minimumCheckedCheckboxCheckedValidator() }
          );

          if (this.simplifiedPaymentP2Enabled) {
            this.paymentMethodAndDateFormGroup = this.formBuilder.group({
              paymentMethod: [
                this.getDefaultPaymentMethod(
                  paymentMethodOptions,
                  otpEntities[key]?.selectedPaymentAccount
                ),
                Validators.compose([Validators.required])
              ],
              paymentDatePicker: [
                this.getDefaultDatePickerValue(otpEntities[key]?.selectedPaymentDate),
                Validators.compose([])
              ]
            });
          }

          billAccountOptions.map(billAccountOption => {
            this.accountSelectionFormGroup.addControl(
              billAccountOption.id,
              this.formBuilder.group({
                paymentOptions: new FormControl({
                  value:
                    billAccountOption.selectedPaymentAmount === undefined
                      ? billAccountOption.paymentOptions[0]
                      : Number(billAccountOption.selectedPaymentAmount) ===
                          Number(billAccountOption.paymentOptions[0].amount.replace(/\$|,/g, ''))
                        ? billAccountOption.paymentOptions[0]
                        : Number(billAccountOption.selectedPaymentAmount) ===
                            Number(billAccountOption.paymentOptions[1].amount.replace(/\$|,/g, ''))
                          ? billAccountOption.paymentOptions[1]
                          : billAccountOption.paymentOptions[2],
                  disabled: !billAccountOption.isSelected
                }),
                otherAmount: new FormControl()
              })
            );
          });
          this.oneTimePayFacade.sendAnalytics({
            pageAnalyticName: this.simplifiedPaymentP2Enabled ? 'Start' : 'ChooseAccount'
          });
          this.spinner.stop();
        }
      );
  }

  getDefaultPaymentMethod(
    paymentMethodOptions: PaymentMethodRadioOption[],
    paymentNickName: string
  ) {
    this.defaultPaymentMethod = paymentMethodOptions.find(
      paymentMethodOption => paymentMethodOption.nickName === paymentNickName
    );
    return this.defaultPaymentMethod ? this.defaultPaymentMethod : paymentMethodOptions[0];
  }

  dateClass: MatCalendarCellClassFunction<Moment> = (cellDate, view) => {
    // Only highligh dates inside the month view.
    if (view === 'month') {
      // Only highlight the desired date.
      return cellDate.date() === this.matDatePickerOptions.highlightDate.getDate()
        ? 'highlight-due-date'
        : '';
    }
    return '';
  };

  getDefaultDatePickerValue(paymentDate: string) {
    if (paymentDate) {
      return new Date(paymentDate);
    }
    // default to today
    return new Date();
  }

  handleCheckBox() {
    this.selectedAccounts = [];
    this.billAccountOptions$.pipe(take(1)).subscribe(billAccountOptions => {
      (<FormArray>this.accountSelectionFormGroup.get('option')).controls.forEach((option, i) => {
        billAccountOptions[i].isSelected = option.value;
      });
      billAccountOptions.forEach(billAccountOption => {
        if (billAccountOption.isSelected) {
          const selectedPaymentOption: PaymentOption = this.accountSelectionFormGroup
            .get(billAccountOption.id)
            .get('paymentOptions').value;
          this.selectedAccounts.push({
            billAccountNumber: billAccountOption.id,
            selectedPaymentAmount: selectedPaymentOption.amount.replace(/\$|,/g, ''),
            dueDate: billAccountOption.dueDate,
            paymentTerm: selectedPaymentOption.id
          });
        }
      });
    });
  }

  handleCheckBoxInteraction() {
    return combineLatest([
      this.billAccountOptions$,
      this.accountSelectionFormGroup.valueChanges.pipe(
        startWith(this.accountSelectionFormGroup.value)
      ),
      this.paymentMethodAndDateFormGroup?.valueChanges.pipe(
        startWith(this.paymentMethodAndDateFormGroup?.value)
      )
    ]).pipe(
      map(([billAccountOptions]) => {
        (<FormArray>this.accountSelectionFormGroup.get('option')).controls.forEach((option, i) => {
          billAccountOptions[i].isSelected = option.value;
        });
        let sum = 0;
        let disableCalender = false;
        billAccountOptions = billAccountOptions.filter(option => option.isSelected);
        if (billAccountOptions.length) {
          billAccountOptions.forEach(billAccountOption => {
            const selectedPaymentOption: PaymentOption = this.accountSelectionFormGroup
              .get(billAccountOption.id)
              .get('paymentOptions').value;
            const selectedPaymentAmount = selectedPaymentOption.amount.replace(/\$|,/g, '');
            sum += Math.round(Number(selectedPaymentAmount) * 100) / 100;
          });
          const hasPastDue = billAccountOptions.some(
            account => account.pastDue && account.isSelected
          );
          const hasMinDueZero = billAccountOptions.some(
            account => account.minDue.toString() === '0' && account.isSelected
          );
          const isDueDateToday = billAccountOptions.some(
            account => isSameDay(new Date(), account.dueDate) && account.isSelected
          );
          const earliestDate = _sortBy(billAccountOptions, 'dueDate') || [];
          const dueDate = earliestDate[0]?.dueDate;
          this.matDatePickerOptions = {
            minDate: new Date(),
            maxDate: new Date(dueDate.replace(/-/g, '/')),
            highlightDate: new Date(dueDate.replace(/-/g, '/'))
          };
          // when the due date is 0001-01-01 or 0000-12-31 that means that there isn't a due date establihsed yet
          disableCalender =
            hasPastDue ||
            hasMinDueZero ||
            isDueDateToday ||
            dueDate === '0001-01-01' ||
            dueDate === '0000-12-31';
        } else {
          // default to today if no accounts are selected and disable the calender until an account is selected
          this.matDatePickerOptions = {
            minDate: new Date(),
            maxDate: new Date(),
            highlightDate: new Date()
          };
          disableCalender = true;
        }
        return { sum, disableCalender };
      })
    );
  }

  editPaymentMethod(id: string): void {
    this.oneTimePayFacade
      .getPaymentMethodById(id)
      .pipe(take(1))
      .subscribe(paymentMethod => {
        this.openPaymentMethodModal({
          accountType: this.paymentMethodUtil.getPaymentMethodAccountType(paymentMethod),
          operation: PaymentMethodAddEditOperationType.EDIT,
          paymentMethod
        });
      });
  }
  addCheckingAccount(): void {
    if (!this.isHomesite)
      this.oneTimePayFacade.sendAnalytics({
        buttonAnalyticName: 'Simplified:AddPaymentMethod:Checking'
      });
    this.openPaymentMethodModal({
      accountType: PaymentMethodAccountType.CHECKING,
      operation: PaymentMethodAddEditOperationType.ADD,
      paymentMethod: null
    });
  }

  addSavingsAccount(): void {
    this.oneTimePayFacade.sendAnalytics({
      buttonAnalyticName: 'Simplified:AddPaymentMethod:Savings'
    });
    this.openPaymentMethodModal({
      accountType: PaymentMethodAccountType.SAVINGS,
      operation: PaymentMethodAddEditOperationType.ADD,
      paymentMethod: null
    });
  }

  addCreditDebit(): void {
    if (!this.isHomesite)
      this.oneTimePayFacade.sendAnalytics({
        buttonAnalyticName: 'Simplified:AddPaymentMethod:DebitCredit'
      });
    this.openPaymentMethodModal({
      accountType: PaymentMethodAccountType.CREDIT_DEBIT,
      operation: PaymentMethodAddEditOperationType.ADD,
      paymentMethod: null
    });
  }

  paymentMethodAddEditComplete(newPaymentAccountId: string): void {
    this.modalService.close('paymentMethodModal');

    if (!this.isHomesite) {
      const newPaymentMethodAnalytics = {
        event: 'new_pay_method',
        payment_type: this.addPaymentMethodType
      };
      this.analyticsFacade.trackEvent(newPaymentMethodAnalytics);
    }

    const correlationId: string = this.utilService.generateId();
    this.spinner.start({
      blockActions: true
    });
    this.oneTimePayFacade.dispatchCorrelationId(correlationId);

    combineLatest([
      this.oneTimePayFacade.getPaymentMethodState(),
      this.oneTimePayFacade.buildPaymentMethodOptions()
    ])
      .pipe(
        map(([paymentMethodState, paymentMethodOptions]) => ({
          paymentMethodState,
          paymentMethodOptions
        })),
        filter(state => state.paymentMethodState.correlationId === correlationId),
        takeUntil(this.stop$)
      )
      .subscribe(state => {
        this.spinner.stop();
        if (!state.paymentMethodState.hasError) {
          const newPaymentMethodOption: PaymentMethodRadioOption = state.paymentMethodOptions.find(
            option => option.id === newPaymentAccountId
          );
          this.paymentMethodOptions = state.paymentMethodOptions;

          this.paymentMethodAndDateFormGroup.get('paymentMethod').setValue(newPaymentMethodOption);
          this.paymentMethodError = false;
          this.paymentMethodAndDateFormGroup.updateValueAndValidity();
        }
      });
  }

  openPaymentMethodModal(modalConfig: PaymentMethodAddEditConfig) {
    this.paymentMethodAddEditConfig$.next(modalConfig);
    this.modalService.open('paymentMethodModal', true);
    let paymentType: string;
    this.addPaymentMethodType = modalConfig.accountType;
    switch (modalConfig.accountType) {
      case PaymentMethodAccountType.CREDIT_DEBIT:
        paymentType = 'DebitCreditCard';
        break;
      default:
        paymentType = 'CheckingSavings';
    }
    if (!this.isHomesite) {
      if (modalConfig.operation === PaymentMethodAddEditOperationType.ADD) {
        setTimeout(() => {
          this.oneTimePayFacade.sendAnalytics({
            pageAnalyticName: `Simplified:AddPaymentMethod:${paymentType}`
          });
        }, 0);
      } else {
        this.oneTimePayFacade.sendAnalytics({
          pageAnalyticName: `Simplified:EditPaymentMethod:${paymentType}`
        });
      }
    }
  }

  submit() {
    if (this.paymentMethodOptions.length === 0) {
      this.paymentMethodError = true;
    }
    if (this.simplifiedPaymentP2Enabled) {
      this.reviewPaymentInfo = {
        paymentMethodNickName:
          this.paymentMethodAndDateFormGroup.get('paymentMethod').value.nickName,
        paymentDate: this.paymentMethodAndDateFormGroup.get('paymentDatePicker').value
      };
    }
    this.handleCheckBox();
    const accounts: BillAccount[] = [];
    this.oneTimePayFacade
      .getBillAccountEntities()
      .pipe(take(1))
      .subscribe(billAccounts =>
        this.selectedAccounts.map(account => {
          accounts.push(billAccounts[account.billAccountNumber]);
        })
      );
    this.oneTimePayFacade.registerBillAccountsBeforeOneTimePay(
      accounts,
      this.selectedAccounts,
      this.reviewPaymentInfo
    );
    this.oneTimePayFacade.sendAnalytics({
      buttonAnalyticName: this.simplifiedPaymentP2Enabled
        ? 'Simplified:PaymentSelection:ReviewPayment'
        : 'Simplified:ChooseAccount:Continue'
    });
  }

  cancel() {
    this.modalService.open(this.cancelModalConfig.modalId);
    this.oneTimePayFacade.sendAnalytics({
      buttonAnalyticName: this.simplifiedPaymentP2Enabled
        ? 'Simplified:Cancel'
        : 'Simplified:ChooseAccount:Cancel'
    });
  }

  stay() {
    this.modalService.close(this.cancelModalConfig.modalId);
    // reset linkEventText
    this.linkEventText = null;
  }

  leave() {
    this.modalService.closeAll();
    this.store.dispatch(
      OneTimePayActions.leaveOTPFlow({
        billAccountNumber: this.alertLinkEventBillAccountNumber,
        id: this.linkEventText
      })
    );
  }

  closeSilentRegistrationErrorModal() {
    this.modalService.closeAll();
  }

  continueWithSuccessfulRegistration() {
    this.modalService.close(this.silentRegistrationPartialError.modalId);
    this.store.dispatch(
      fromRouterActions.Go({
        path: [BillingPaymentPaths.REVIEW_ONE_TIME_PAYMENT]
      })
    );
  }

  linkTextEvent(linkEventObj: { text: string; billAccountNumber: string }) {
    this.modalService.open(this.cancelModalConfig.modalId);
    this.linkEventText = linkEventObj.text;
    this.alertLinkEventBillAccountNumber = linkEventObj.billAccountNumber;
  }

  updateAccountEvent(data: {
    billAccountOptions: AccountForSimplifiedPayment[];
    billAccountOption: AccountForSimplifiedPayment;
  }) {
    this.billAccountOptions$ = this.oneTimePayFacade.getUpdatedSimplifiedPaymentBillAccounts(
      of(data.billAccountOptions),
      data.billAccountOption
    );
  }

  openRomeModal() {
    this.analyticsFacade.trackButtonClick(OverviewAnalytics.clickRomeLearnMore);
    this.modalService.open(this.romeBannerPaymentModalId);
    setTimeout(() => {
      this.analyticsFacade.trackPage(OverviewAnalytics.romeModalOpen);
    }, 1);
  }

  ngOnDestroy() {
    this.stop$.next();
    this.stop$.complete();
  }
}
